import { useAppContext } from 'contexts/AppContext';
import humps from 'humps';
import React from 'react';

const useResources = () => {
  const { appState } = useAppContext();

  const sort = (arr, key = 'name', desc = false) => {
    const i = desc ? -1 : 1;
    return arr.sort((a, b) => (a[key] > b[key] ? i : -i));
  };

  const makeAccounting = (accounting) => {
    const paymentTerms = sort(accounting.paymentTerms);
    const taxes = sort(accounting.taxes);
    const currencies = sort(accounting.currencies);

    return { paymentTerms, taxes, currencies };
  };

  const makeItemMasterData = (itemMasterData, taxes) => {
    const items = sort(itemMasterData.items.filter(x => x.showInSalesApp));
    const itemTags = [...new Set(items.reduce((pre, cur) => [...pre, ...(cur.tags || [])], []))].sort();
    const itemGroups = sort(itemMasterData.itemGroups);
    const itemTypes = sort(itemMasterData.itemTypes);
    const uoms = sort(itemMasterData.uoms);

    itemGroups.forEach((itemGroup) => {
      itemGroup.abstract = !!itemGroups.filter(
        (x) => x.parentGroup === itemGroup.id
      ).length;
    });

    itemGroups.forEach((itemGroup) => {
      if (!!itemGroup.parentGroup) {
        const parent = itemGroups.find((x) => x.id === itemGroup.parentGroup);
        itemGroup.parentGroup = {
          id: parent.id,
          name: parent.name,
        };
      }
    });

    uoms.forEach((uom) => {
      if (!!uom.baseUoM) {
        const baseUoM = uoms.find((x) => x.id === uom.baseUoM);
        uom.baseUoM = {
          id: baseUoM.id,
          name: baseUoM.name,
        };
      }
    });

    items.forEach((item) => {
      item.itemGroup = itemGroups.find((x) => x.id === item.itemGroup);
      item.itemType = itemTypes.find((x) => x.id === item.itemType);
      item.uom = uoms.find((x) => x.id === item.uom);
      item.salesPriceUom = uoms.find((x) => x.id === item.salesPriceUom);
      item.purchaseTax = taxes.find((x) => x.id === item.purchaseTax);
      item.salesTax = taxes.find((x) => x.id === item.salesTax);

      const itemField = appState.auth.user.entity.config.primaryItemField;
      const secondItemField =
        appState.auth.user.entity.config.secondaryItemField;

      item.displayName = item[humps.camelize(itemField)];
      item.secondaryDisplayName = item[humps.camelize(secondItemField)] || '';
    });

    return {
      items,
      salesItems: items.filter((x) => x.active && x.salesItem),
      purchaseItems: items.filter((x) => x.active && x.purchaseItem),
      itemGroups,
      uoms,
      itemTags
    };
  };

  const makeContacts = (contacts, paymentTerms, currencies) => {
    const companies = sort(contacts.companies);
    const branches = contacts.companyBranches;

    branches.forEach((branch) => {
      branch.address = [
        branch.addressLine1,
        branch.addressLine2,
        branch.addressLine3,
        branch.addressLine4,
      ]
        .filter((x) => !!x)
        .join(', ');
    });

    companies.forEach((el) => {
      el.companyFinancials = {
        PaymentTerms: paymentTerms.find(
          (x) => x.id === el.companyFinancialsPaymentTerms
        ),
        currency: currencies.find((x) => x.id === el.companyFinancialsCurrency),
      };
      el.branches = sort(branches.filter((x) => x.company === el.id));
    });

    return {
      companies,
      customers: companies.filter((x) => x.customer),
      suppliers: companies.filter((x) => x.supplier),
    };
  };

  return { makeAccounting, makeItemMasterData, makeContacts };
};

export default useResources;
